<template>
  <div class="push-notification-settings">
    <b-card title="Push Notifications" class="mb-3">
      <b-card-text>
        <div v-if="!pushNotificationSupported" class="text-muted">
          <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
          Your browser does not support push notifications.
        </div>
        
        <div v-else>
          <div class="d-flex align-items-center justify-content-between mb-3">
            <div>
              <h6 class="mb-1">Enable Push Notifications</h6>
              <small class="text-muted">
                Receive notifications about important updates and alerts
              </small>
            </div>
            <b-form-checkbox
              v-model="notificationsEnabled"
              switch
              size="lg"
              @change="toggleNotifications"
              :disabled="pushNotificationPermission === 'denied'"
            >
            </b-form-checkbox>
          </div>
          
          <div v-if="pushNotificationPermission === 'denied'" class="alert alert-warning">
            <b-icon icon="exclamation-triangle" class="mr-2"></b-icon>
            You have blocked notifications. Please enable them in your browser settings.
          </div>
          
          <div v-if="pushNotificationsEnabled" class="mt-3">
            <b-alert show variant="success">
              <b-icon icon="check-circle" class="mr-2"></b-icon>
              Push notifications are enabled
            </b-alert>
            
            <b-button 
              variant="outline-primary" 
              size="sm"
              @click="handleTestNotification"
              :disabled="testLoading"
            >
              <b-spinner v-if="testLoading" small class="mr-1"></b-spinner>
              Send Test Notification
            </b-button>
          </div>
        </div>
      </b-card-text>
    </b-card>
  </div>
</template>

<script>
import pushNotifications from '@/mixins/pushNotifications'

export default {
  name: 'PushNotificationSettings',
  
  mixins: [pushNotifications],
  
  data() {
    return {
      notificationsEnabled: false,
      testLoading: false
    }
  },
  
  watch: {
    pushNotificationsEnabled: {
      immediate: true,
      handler(val) {
        this.notificationsEnabled = val
      }
    }
  },
  
  methods: {
    async toggleNotifications(enabled) {
      try {
        if (enabled) {
          // Request permission and subscribe
          const permission = await this.requestPushNotificationPermission()
          if (permission !== 'granted') {
            this.notificationsEnabled = false
            this.$bvToast.toast('Notification permission denied', {
              title: 'Notifications',
              variant: 'warning'
            })
          } else {
            this.$bvToast.toast('Push notifications enabled', {
              title: 'Notifications',
              variant: 'success'
            })
          }
        } else {
          // Unsubscribe
          await this.unsubscribeFromPushNotifications()
          this.$bvToast.toast('Push notifications disabled', {
            title: 'Notifications',
            variant: 'info'
          })
        }
      } catch (error) {
        console.error('Error toggling notifications:', error)
        this.notificationsEnabled = !enabled // Revert on error
        this.$bvToast.toast('Failed to update notification settings', {
          title: 'Error',
          variant: 'danger'
        })
      }
    },
    
    async handleTestNotification() {
      this.testLoading = true
      try {
        await this.sendTestNotification()  // Call mixin method
        this.$bvToast.toast('Test notification sent!', {
          title: 'Success',
          variant: 'success'
        })
      } catch (error) {
        this.$bvToast.toast('Failed to send test notification', {
          title: 'Error',
          variant: 'danger'
        })
      } finally {
        this.testLoading = false
      }
    }
  }
}
</script>

<style scoped>
.push-notification-settings {
  max-width: 600px;
}
</style>